import { useEffect, useId, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Search, UsersRound } from "@fg/icons";
import type { PresenceStatus } from "@fg/ui-theme";
import { connectParityApi, type ChatListItem } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { cn } from "@/lib/utils";
import type { AppMode } from "./AppRail";
import { ChatAvatar } from "../chat/chatUi";

/** What the switcher can do in the app; the shell supplies these. */
export interface SwitcherActions {
    openChat: (key: string) => void;
    openPerson: (userId: string) => void;
    setMode: (mode: AppMode) => void;
    showUnread: () => void;
    setStatus: (status: PresenceStatus) => void;
    search: (query: string) => void;
    showHelp: () => void;
}

interface Item {
    id: string;
    label: string;
    hint?: string;
    group: string;
    chat?: ChatListItem;
    run: () => void;
}

const MODES: { mode: AppMode; label: string; words: string }[] = [
    { mode: "dms", label: "Chat", words: "chats messages dm" },
    { mode: "teams", label: "Channels", words: "channels teams" },
    { mode: "groups", label: "Groups", words: "group chats" },
    { mode: "calendar", label: "Calendar", words: "meetings schedule" },
    { mode: "history", label: "Calls", words: "history recordings" },
    { mode: "saved", label: "Saved messages", words: "bookmarks" },
    { mode: "activity", label: "Activity", words: "notifications mentions" }
];

const STATUS_COMMANDS: { cmd: string; status: PresenceStatus; label: string }[] = [
    { cmd: "available", status: "online", label: "Set your status to Available" },
    { cmd: "busy", status: "busy", label: "Set your status to Busy" },
    { cmd: "dnd", status: "dnd", label: "Set your status to Do not disturb" },
    { cmd: "away", status: "away", label: "Set your status to Away" },
    { cmd: "brb", status: "brb", label: "Set your status to Be right back" },
    { cmd: "offline", status: "offline", label: "Appear offline" }
];

/**
 * The Teams command box in one palette (Ctrl+K, Ctrl+G or Ctrl+/): type a name to jump to a chat or person, or start with "/" for
 * commands — /available /busy /dnd /away /brb /offline, /mentions, /unread, /saved, /calendar, /calls, /goto, /chat, /help.
 */
export function QuickSwitcher({ chats, actions, onClose, initial = "" }: {
    chats: ChatListItem[];
    actions: SwitcherActions;
    onClose: () => void;
    initial?: string;
}) {
    const token = useAccessToken();
    const listId = useId();
    const inputRef = useRef<HTMLInputElement>(null);
    const [text, setText] = useState(initial);
    const [active, setActive] = useState(0);
    useEffect(() => { inputRef.current?.focus(); }, []);

    const slash = text.startsWith("/");
    const [cmd, ...rest] = slash ? text.slice(1).split(/\s+/) : [""];
    const arg = rest.join(" ").trim();
    const needle = (slash ? (cmd === "goto" || cmd === "chat" || cmd === "call" ? arg : "") : text).trim().toLowerCase();

    const [debounced, setDebounced] = useState("");
    useEffect(() => {
        const t = window.setTimeout(() => setDebounced(needle), 200);
        return () => window.clearTimeout(t);
    }, [needle]);
    const people = useQuery({
        queryKey: ["switcher-dir", debounced, token],
        queryFn: () => connectParityApi.searchDirectory(debounced, token),
        enabled: Boolean(token) && debounced.length >= 2
    });

    const done = (fn: () => void) => () => { onClose(); fn(); };

    const items = useMemo<Item[]>(() => {
        const out: Item[] = [];
        const chatItems = (q: string): Item[] =>
            chats
                .filter((c) => !q || c.title.toLowerCase().includes(q) || (c.peerEmail ?? "").toLowerCase().includes(q) || c.memberNames.some((n) => n.toLowerCase().includes(q)))
                .slice(0, q ? 8 : 6)
                .map((c) => ({ id: `chat-${c.kind}-${c.id}`, label: c.title, hint: c.kind === "dm" ? c.peerEmail ?? "Chat" : c.kind === "group" ? "Group chat" : "Meeting chat", group: "Chats", chat: c, run: done(() => actions.openChat(`${c.kind}:${c.id}`)) }));
        if (slash) {
            const c = (cmd ?? "").toLowerCase();
            for (const s of STATUS_COMMANDS) {
                if (s.cmd.startsWith(c)) out.push({ id: `cmd-${s.cmd}`, label: `/${s.cmd}`, hint: s.label, group: "Commands", run: done(() => actions.setStatus(s.status)) });
            }
            const nav: [string, string, () => void][] = [
                ["mentions", "Show your mentions in Activity", () => actions.setMode("activity")],
                ["activity", "Open Activity", () => actions.setMode("activity")],
                ["unread", "Show unread chats", actions.showUnread],
                ["saved", "Open saved messages", () => actions.setMode("saved")],
                ["calendar", "Open the calendar", () => actions.setMode("calendar")],
                ["calls", "Open call history", () => actions.setMode("history")],
                ["teams", "Open channels", () => actions.setMode("teams")],
                ["help", "Keyboard shortcuts and commands", actions.showHelp],
                ["keys", "Keyboard shortcuts", actions.showHelp]
            ];
            for (const [name, hint, fn] of nav) {
                if (name.startsWith(c)) out.push({ id: `cmd-${name}`, label: `/${name}`, hint, group: "Commands", run: done(fn) });
            }
            if ("goto".startsWith(c) || "chat".startsWith(c) || c === "call") {
                if (c === "goto" || c === "chat") out.push(...chatItems(needle));
                else out.push({ id: "cmd-goto", label: "/goto  name", hint: "Jump to a chat or a person", group: "Commands", run: () => setText("/goto ") });
            }
            return out;
        }
        const q = needle;
        out.push(...chatItems(q));
        const modes = MODES.filter((m) => !q || m.label.toLowerCase().includes(q) || m.words.includes(q));
        for (const m of modes) out.push({ id: `mode-${m.mode}`, label: m.label, hint: "Go to", group: "Go to", run: done(() => actions.setMode(m.mode)) });
        const known = new Set(chats.map((c) => (c.peerEmail ?? "").toLowerCase()).filter(Boolean));
        for (const u of (people.data ?? []).filter((p) => !known.has(p.email.toLowerCase())).slice(0, 5)) {
            out.push({ id: `person-${u.userId}`, label: u.displayName || u.email, hint: u.email, group: "People", run: done(() => actions.openPerson(u.userId)) });
        }
        if (q.length >= 2) out.push({ id: "search", label: `Search messages for “${text.trim()}”`, hint: "Press Enter", group: "Search", run: done(() => actions.search(text.trim())) });
        return out;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chats, slash, cmd, needle, people.data, text]);

    useEffect(() => setActive(0), [text, items.length]);

    const onKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
        if (e.key === "ArrowDown" || e.key === "ArrowUp") {
            e.preventDefault();
            const n = items.length;
            if (n > 0) setActive((a) => (e.key === "ArrowDown" ? (a + 1) % n : (a - 1 + n) % n));
            return;
        }
        if (e.key === "Enter" && !e.nativeEvent.isComposing) {
            e.preventDefault();
            // A fully typed status command runs even when the list shows a longer match.
            items[active]?.run();
        }
    };

    let lastGroup = "";
    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Quick switcher"
            className="fixed inset-0 z-[60] flex items-start justify-center bg-black/40 p-4 pt-[12vh]"
            onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}
        >
            <div className="w-full max-w-xl overflow-hidden rounded-lg border bg-popover text-popover-foreground shadow-2xl">
                <div className="flex items-center gap-2 border-b px-3">
                    <Search className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                    <input
                        ref={inputRef}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        onKeyDown={onKeyDown}
                        role="combobox"
                        aria-expanded
                        aria-controls={listId}
                        aria-activedescendant={items[active] ? `${listId}-${active}` : undefined}
                        aria-label="Go to a chat or person, or type / for commands"
                        placeholder="Go to a chat or person, or type / for commands"
                        className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                    />
                    <kbd className="rounded border px-1.5 text-[10px] text-muted-foreground">Esc</kbd>
                </div>
                <ul id={listId} role="listbox" className="max-h-[50vh] overflow-y-auto p-1">
                    {items.length === 0 ? (
                        <li className="px-3 py-6 text-center text-sm text-muted-foreground">{slash ? "No such command. Type /help." : "Nothing matches."}</li>
                    ) : items.map((it, i) => {
                        const header = it.group !== lastGroup;
                        lastGroup = it.group;
                        return (
                            <li key={it.id} role="presentation">
                                {header ? <div className="px-2 pb-0.5 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{it.group}</div> : null}
                                <div
                                    id={`${listId}-${i}`}
                                    role="option"
                                    aria-selected={i === active}
                                    onMouseEnter={() => setActive(i)}
                                    onMouseDown={(e) => { e.preventDefault(); it.run(); }}
                                    className={cn("flex cursor-pointer items-center gap-2.5 rounded-md px-2 py-1.5 text-sm", i === active ? "bg-accent" : "hover:bg-accent/60")}
                                >
                                    {it.chat ? <ChatAvatar name={it.chat.title} group={it.chat.kind === "group"} meeting={it.chat.kind === "meeting"} size="sm" /> : it.group === "People" ? <UsersRound className="h-4 w-4 text-muted-foreground" aria-hidden /> : <span className="w-4" />}
                                    <span className="min-w-0 flex-1 truncate">{it.label}</span>
                                    {it.hint ? <span className="max-w-[55%] truncate text-xs text-muted-foreground">{it.hint}</span> : null}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            </div>
        </div>
    );
}
